import { NumberFormatter } from '@mantine/core';
import { FileSearch, Infinity } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'shared/api';
import { createRegistry, RegistryRootProps } from 'shared/ui';
import { WalletRegistryModel } from '../../model';

export type WalletRegistryProps = RegistryRootProps & {
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (wallet: Wallet) => ReactNode;
	emptyActionSlot?: ReactNode;
};

export const createWalletRegistry = (model: WalletRegistryModel) => {
	const Registry = createRegistry(model);

	const WalletRegistry = forwardRef<HTMLDivElement, WalletRegistryProps>(
		({ emptyActionSlot, tableTdActionSlot, topBarActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.TopBar>{topBarActionSlot}</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='currency'>
									{t('wallet-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>{t('wallet-registry.table-columns.balance')}</Registry.Table.Th>
								<Registry.Table.Th>{t('wallet-registry.table-columns.actions')}</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({ variant: row.isLocked ? 'locked' : 'default' })}
							>
								{(row) => (
									<>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											{row.balance !== null ? (
												<NumberFormatter value={row.balance} />
											) : (
												<Infinity style={{ fontSize: 16 }} />
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('wallet-registry.empty.title')}
						message={t('wallet-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('wallet-registry.table-empty.title')}
						message={t('wallet-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return WalletRegistry;
};
